import { Truthy } from 'lodash';

export function unique<TType>(value: TType, index: number, self: TType[]) {
  return self.indexOf(value) === index;
}

type idAccessorFn<TType, TIdentifier = number | string> = (value: TType) => TIdentifier;

/**
 * Much like {@link unique}, this function allows you to ensure all items in an
 * array are unique. Specify a {@link getUniqueId} to access a property on the
 * array item which specifies it as unique, and no 2 items in the resulting array
 * will have the same uniqueId. Implemented in such a way that the first instance
 * of each unique item is the item that is kept.
 *
 * @param getUniqueId
 * @returns An array with items which do not have shared unique identifiers as
 *  defined by {@link getUniqueId}
 */
export function makeUniqueFilter<TType>(getUniqueId: idAccessorFn<TType>) {
  return (targetValue: TType, targetIndex: number, arr: TType[]): boolean => {
    const targetUniqueId = getUniqueId(targetValue);
    return arr.findIndex((val) => getUniqueId(val) === targetUniqueId) === targetIndex;
  };
}

/**
 * An alternate interface for {@link makeUniqueFilter} which allows you to pass
 * in an equality function rather than a getUniqueId function.
 */
export function makeUniqueFilterEq<TType>(eq: (a: TType, b: TType) => boolean) {
  return (targetValue: TType, targetIndex: number, arr: TType[]): boolean => {
    return arr.findIndex((val) => eq(val, targetValue)) === targetIndex;
  };
}

export function truthy<TType>(value: TType): value is Truthy<TType> {
  return Boolean(value);
}

/**
 * For a given array, filter only the first n items which match the given matcher.
 * This is useful for avoiding unnecessary work when you only need to find the first n.
 */
export const findFirstN = <TData>(
  arr: TData[],
  matcher: (data: TData) => boolean,
  n = 1,
) => {
  const res: TData[] = [];
  if (n < 1) return res;
  for (const i in arr) {
    const el = arr[i];
    const match = matcher(el);
    if (match) res.push(el);
    if (res.length >= n) return res;
  }
  return res;
};
